import { v4 as uuid } from 'uuid';
import {
  ChimeSDKMessagingClient,
  ListChannelMessagesCommand,
  SendChannelMessageCommand,
  UpdateChannelMessageCommand,
  RedactChannelMessageCommand,
} from '@aws-sdk/client-chime-sdk-messaging';
import {
  LogLevel,
  ConsoleLogger,
  DefaultMessagingSession,
  MessagingSessionConfiguration,
} from 'amazon-chime-sdk-js';

class MessagingService {
  _session: any;
  sessionId: string;
  _logger: any;
  _messageUpdateCallbacks: any;
  _endpoint: string;
  _userArn: string;
  _userToken: string;
  _chime: any;
  fetchRecentMessage: (message) => void;

  constructor(fetchRecentMessage, userArn, userToken) {
    this.fetchRecentMessage = fetchRecentMessage;
    this._userArn = userArn;
    this._userToken = userToken;
    this.sessionId = uuid();
    this._logger = new ConsoleLogger('SDK Chat Demo', LogLevel.INFO);
    this._messageUpdateCallbacks = [];
  }

  messageObserver = {
    messagingSessionDidStart: () => {
      console.info('Messaging Connection started!');
    },
    messagingSessionDidStartConnecting: reconnecting => {
      console.info('Messaging Connection connecting');
    },
    messagingSessionDidStop: event => {
      console.info('Messaging Connection received DidStop event');
    },
    messagingSessionDidReceiveMessage: message => {
      console.info('Messaging Connection received message');
      this.fetchRecentMessage(JSON.parse(message.payload));
      this.publishMessageUpdate(message);
    },
  };

  setMessagingEndpoint(member) {
    this._endpoint = member?.endpoint?.Url;

    const mutableCreds = {
      accessKeyId: member?.credentials?.accessKeyId,
      secretAccessKey: member?.credentials?.secretAccessKey,
      sessionToken: member?.credentials?.sessionToken,
    };

    const chime = new ChimeSDKMessagingClient({
      region: member?.region,
      credentials: mutableCreds,
    });

    this._chime = chime;

    const sessionConfig = new MessagingSessionConfiguration(
      this._userArn,
      this.sessionId,
      this._endpoint,
      this._chime,
    );

    this._session = new DefaultMessagingSession(sessionConfig, this._logger);
    this._session.addObserver(this.messageObserver);
    this._session.start();
  }

  connect(member) {
    this.setMessagingEndpoint(member);
  }

  close() {
    try {
      this._session.stop();
    } catch (err) {
      console.error('Failed to stop Messaging Session.');
    }
  }

  subscribeToMessageUpdate(callback) {
    console.info('Message listener subscribed!');
    this._messageUpdateCallbacks.push(callback);
  }

  unsubscribeFromMessageUpdate(callback) {
    const index = this._messageUpdateCallbacks.indexOf(callback);
    if (index !== -1) {
      this._messageUpdateCallbacks.splice(index, 1);
    }
  }

  publishMessageUpdate(message) {
    console.info('Sending message update to listeners!');
    for (let i = 0; i < this._messageUpdateCallbacks.length; i += 1) {
      const callback = this._messageUpdateCallbacks[i];
      callback(message);
    }
  }

  async sendMessage(channelArn, content, metadata = {}) {
    try {
      const command = new SendChannelMessageCommand({
        ChannelArn: channelArn,
        Content: content,
        Persistence: 'PERSISTENT',
        Type: 'STANDARD',
        ChimeBearer: this._userArn,
        Metadata: JSON.stringify(metadata),
        ClientRequestToken: crypto.randomUUID(),
      });

      const response = await this._chime.send(command);
      return response;
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  }

  async fetchChannelMessages(
    channelArn,
    nextToken = null,
    maxResults = 20,
    sortOrder = 'DESCENDING',
  ) {
    try {
      const command = new ListChannelMessagesCommand({
        ChannelArn: channelArn,
        ChimeBearer: this._userArn,
        MaxResults: maxResults,
        NextToken: nextToken,
        SortOrder: sortOrder as 'ASCENDING' | 'DESCENDING',
      });

      const response = await this._chime.send(command);
      return response;
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      throw error;
    }
  }

  async editMessage(channelArn, messageId, newContent, metadata = {}) {
    try {
      const command = new UpdateChannelMessageCommand({
        ChannelArn: channelArn,
        MessageId: messageId,
        Content: newContent,
        ChimeBearer: this._userArn,
        Metadata: JSON.stringify(metadata),
      });

      const response = await this._chime.send(command);
      return response;
    } catch (error) {
      console.error('Failed to edit message:', error);
      throw error;
    }
  }

  async deleteMessage(channelArn, messageId) {
    try {
      const command = new RedactChannelMessageCommand({
        ChannelArn: channelArn,
        MessageId: messageId,
        ChimeBearer: this._userArn,
      });

      const response = await this._chime.send(command);
      return response;
    } catch (error) {
      console.error('Failed to delete message:', error);
      throw error;
    }
  }
}

export default MessagingService;
